/**
 * Turn one ManufactureDraft into the `info` object `saveNewManufacture` consumes.
 *
 * An imported manufacture must be indistinguishable from one entered on
 * ManufactureDetailScreen, or stock, cost and dailyStats disagree. So this mirrors that
 * screen's `save()` field by field and prices with the same helper
 * (`calculateTotalPurchasePriceFromIngredients`) its `chooseProduct` uses:
 *  - recipe lines are `{...productsDict[line.id], quantity: line.quantity}`, as chooseProduct
 *    builds them;
 *  - `product` is `{...product, ingredients, purchasePrice}` with the per-unit cost;
 *  - `totalSum` is that per-unit cost × quantity.
 *
 * Differences from hand entry, all deliberate:
 *  - `date` is the file's date at store-local time (`storeLocalDateAtTime`, a date-only cell lands
 *    at midday so the autoclose cutoff cannot push it to the previous day) and `dailyStatID` is the
 *    STORE's business day of that instant;
 *  - provenance: `manufactureSource: 'import'` + `importBatchId`;
 *  - `batchNumber` only when the file gave one — no key is ever `undefined`.
 */

/* eslint-disable @typescript-eslint/no-explicit-any -- product docs are untyped across Firestore/Supabase read modes (same ruling as utils/salesImport/resolve.ts) */

import { calculateTotalPurchasePriceFromIngredients } from '../ingredientCalculations'
import { getDailyStatID, storeLocalDateAtTime } from '../timezoneUtils'
import { recipeLineQuantity } from './group'
import { type ManufactureDraft } from './types'

export interface BuildManufactureInfoContext {
  /** Store the run writes to (its id). */
  store: string
  organizationId: string
  cashierUID: string
  /** One id per run (`createImportBatchId()`), stamped on every manufacture it creates. */
  importBatchId: string
  /** The catalog the recipe lines are looked up in (productsDictAtom). */
  productsDict: Record<string, any>
}

/** The payload `saveNewManufacture(db, info)` receives. */
export interface ManufactureInfo {
  product: any
  quantity: number
  ingredientsEdited: boolean
  ingredients: any[]
  totalSum: number
  date: Date
  dailyStatID: string
  cashierUID: string
  store: string
  organizationId: string
  batchNumber?: string
  manufactureSource: 'import'
  importBatchId: string
}

/**
 * A recipe line as chooseProduct builds it. When the ingredient is no longer in the catalog the
 * line keeps the recipe's own id (and name, when it has one): the writer then refuses the whole
 * manufacture with INGREDIENT_NOT_FOUND before writing anything, and the report can name it.
 */
const recipeLine = (line: any, productsDict: Record<string, any>): any => {
  // groupManufactures refused every recipe with an unusable quantity (INVALID_RECIPE), so this
  // only turns a pre-ATL-633 numeric string into the number the backend's @IsNumber() expects.
  const quantity = recipeLineQuantity(line?.quantity)
  if (quantity === null) throw new Error('INVALID_RECIPE')
  const catalogProduct = productsDict[line?.id]
  if (catalogProduct) return { ...catalogProduct, quantity }
  return {
    id: line?.id,
    ...(typeof line?.name === 'string' ? { name: line.name } : {}),
    quantity,
  }
}

export const buildManufactureInfo = (
  draft: ManufactureDraft,
  ctx: BuildManufactureInfoContext
): ManufactureInfo => {
  const { store, organizationId, cashierUID, importBatchId, productsDict } = ctx
  // getDailyStatID(null, …) silently falls back to the device user's store — the ATL-1684 bug.
  if (!store) throw new Error('STORE_REQUIRED')

  const ingredients =
    draft.ingredients === null
      ? (Array.isArray(draft.product?.ingredients) ? draft.product.ingredients : []).map((line: any) =>
          recipeLine(line, productsDict)
        )
      : draft.ingredients.map((line) => ({ ...line.product, quantity: line.perUnit }))

  const purchasePrice: number = calculateTotalPurchasePriceFromIngredients(ingredients)
  const date = storeLocalDateAtTime(store, draft.date)
  const dailyStatID = getDailyStatID(store, date)
  if (dailyStatID === null) throw new Error('STORE_REQUIRED')

  return {
    product: { ...draft.product, ingredients, purchasePrice },
    quantity: draft.quantity,
    ingredientsEdited: draft.ingredients !== null,
    ingredients,
    totalSum: purchasePrice * draft.quantity,
    date,
    dailyStatID,
    cashierUID,
    store,
    organizationId,
    ...(draft.batchNumber !== null ? { batchNumber: draft.batchNumber } : {}),
    manufactureSource: 'import',
    importBatchId,
  }
}
